import { useEffect, useState, type FormEvent } from 'react';
import { api, type AuthMeDTO } from './api';
import { ApiStatus, ErrorBox, Loading } from './components';
import { CompanySettingsPage, DianxiaomiSettingsPage, EmployeeSettingsPage, ExtensionSettingsPage, ShopsPage } from './pages/admin';
import { ProductDetailPage, ProductLibraryPage } from './pages/products';
import { BatchDetailPage, BatchesPage, OverviewPage, QueuePage } from './pages/work';
import { LogsPage } from './pages/logs';
import { OrdersPage } from './pages/orders';
import { OrderProfitPage } from './pages/order-profit';
import { CompanyOrderProfitPage } from './pages/company-order-profit';
import { workStatsStore } from './work-stats-store';
import { readAuthWithRecovery } from './auth-recovery';

type SessionState = 'loading' | 'signed_out' | 'ready' | 'error';
type NavItem = { id: string; label: string; icon: string; href: string; group: string; admin?: boolean; employee?: boolean };

const navItems: NavItem[] = [
  { id: 'overview', label: '工作总览', icon: '⌂', href: '/overview', group: '工作台' },
  { id: 'orders', label: '订单管理', icon: '▣', href: '/orders', group: '运营' },
  { id: 'products', label: '员工商品库', icon: '▦', href: '/products', group: '采集发布' },
  { id: 'preprocess', label: '预处理队列', icon: '↻', href: '/preprocess', group: '采集发布' },
  { id: 'publish', label: '发布队列', icon: '↗', href: '/publish-queue', group: '采集发布' },
  { id: 'batches', label: '发布批次', icon: '▤', href: '/batches', group: '采集发布' },
  { id: 'shops', label: '店铺与分配', icon: '⌑', href: '/shops', group: '公司管理' },
  { id: 'order-profit', label: '订单利润', icon: '◷', href: '/order-profit', group: '公司管理', admin: true },
  { id: 'company-settings', label: '公司服务配置', icon: '⚙', href: '/settings/company', group: '公司管理', admin: true },
  { id: 'dxm-settings', label: '店小秘连接', icon: '↔', href: '/settings/dianxiaomi', group: '公司管理', admin: true },
  { id: 'employee-settings', label: '员工默认设置', icon: '◌', href: '/settings/employee', group: '我的设置', employee: true },
  { id: 'extension', label: '浏览器助手', icon: '⌁', href: '/settings/extension', group: '我的设置' },
  { id: 'trash', label: '回收站', icon: '⌫', href: '/trash', group: '我的设置' },
  { id: 'logs', label: '日志与排查', icon: '≋', href: '/logs', group: '排障' },
];

function currentPath(): string {
  return window.location.pathname === '/' ? '/overview' : window.location.pathname;
}

function routeTitle(path: string): string {
  if (path.startsWith('/products/')) return '商品详情';
  if (path.startsWith('/batches/')) return '发布批次详情';
  return navItems.find((item) => item.href === path)?.label ?? '工作总览';
}

export default function App() {
  const [session, setSession] = useState<SessionState>('loading');
  const [auth, setAuth] = useState<AuthMeDTO | null>(null);
  const [sessionError, setSessionError] = useState<Error | null>(null);
  const [path, setPath] = useState(currentPath());

  async function refreshSession(): Promise<void> {
    setSession('loading'); setSessionError(null);
    try {
      const value = await readAuthWithRecovery(() => api.getAuth());
      workStatsStore.setIdentity(value.user);
      setAuth(value); api.setCsrfToken(value.csrfToken);
      setSession(value.user ? 'ready' : 'signed_out');
    } catch (reason) {
      workStatsStore.setIdentity(null);
      api.setCsrfToken(null); setAuth(null);
      setSessionError(reason instanceof Error ? reason : new Error('无法读取登录状态'));
      setSession('error');
    }
  }

  useEffect(() => { void refreshSession(); }, []);
  useEffect(() => {
    const onPopState = (): void => setPath(currentPath());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  function navigate(nextPath: string): void {
    window.history.pushState({}, '', nextPath);
    setPath(nextPath);
  }

  async function login(input: { loginAccount: string; password: string; role: 'employee' | 'company_admin' }): Promise<void> {
    const result = await api.login(input);
    api.setCsrfToken(result.csrfToken);
    await refreshSession();
    setPath('/overview');
    window.history.replaceState({}, '', '/overview');
  }

  async function logout(): Promise<void> {
    setSessionError(null);
    try {
      await api.logout();
      workStatsStore.setIdentity(null);
      api.setCsrfToken(null); setAuth(null); setSession('signed_out'); navigate('/overview');
    } catch (reason) {
      setSessionError(reason instanceof Error ? reason : new Error('退出登录失败'));
    }
  }

  if (session === 'loading') return <div className="login-form-side"><Loading label="正在确认登录状态…" /></div>;
  if (session === 'error') return <div className="login-form-side"><div className="login-form"><h2>ERP 服务暂时无法连接</h2><p>请检查网络连接或稍后重试。</p><ErrorBox error={sessionError} onRetry={() => void refreshSession()} /></div></div>;
  if (session === 'signed_out' || !auth?.user) return <LoginScreen auth={auth} error={sessionError} onLogin={login} />;

  const user = auth.user;
  const isAdmin = user.role === 'company_admin' || user.role === 'super_admin';
  const globalMode = user.role === 'super_admin' && !user.companyId;
  const globalPageIds = new Set(['overview', 'orders', 'products', 'preprocess', 'publish', 'batches', 'order-profit']);
  const visibleItems = navItems.filter((item) => (!globalMode || globalPageIds.has(item.id)) && (!item.admin || isAdmin) && (!item.employee || !isAdmin));
  const grouped = new Map<string, NavItem[]>();
  for (const item of visibleItems) grouped.set(item.group, [...(grouped.get(item.group) ?? []), item]);
  const page = routeTitle(path);

  return <div className="app-shell">
    <aside className="sidebar">
      <div className="brand"><div className="brand-mark">W</div><div><div className="brand-name">WB ERP</div><div className="brand-caption">采集与发布工作台</div></div></div>
      <nav aria-label="主导航">
        {[...grouped.entries()].map(([group, items]) => <div key={group}>
          <div className="nav-group">{group}</div>
          <div className="nav-list">{items.map((item) => <button key={item.id} className={`nav-button${path === item.href || (item.href === '/products' && path.startsWith('/products/')) || (item.href === '/batches' && path.startsWith('/batches/')) ? ' active' : ''}`} onClick={() => navigate(item.href)}><span className="nav-icon">{item.icon}</span>{item.label}</button>)}</div>
        </div>)}
      </nav>
      <div className="sidebar-spacer" />
      <div className="sidebar-note"><strong>服务端运行状态</strong><br />任务由 API 与 Worker 持久处理。关闭浏览器不影响已接收任务。</div>
    </aside>
    <div className="main-column">
      <header className="topbar"><div className="breadcrumbs">WB ERP <span style={{ margin: '0 8px', color: '#b4bdc6' }}>/</span><strong>{page}</strong></div><div className="top-actions"><div className="identity"><div className="avatar">{user.displayName.slice(0, 1) || 'U'}</div><div><div className="identity-name">{user.displayName}</div><div className={`identity-meta${user.role === 'super_admin' ? ' super-admin-identity' : ''}`}>{user.role === 'super_admin' ? `超级管理员 / ${user.companyId ? `当前代管：${user.companyName ?? user.companyId}` : '全部公司只读'}` : `${user.companyName} · ${isAdmin ? '公司管理员' : '员工'}`}</div></div></div>{user.role === 'super_admin' && <SuperAdminCompanySwitcher user={user} onSwitched={refreshSession} />}<button className="button button-small" onClick={() => void logout()}>退出</button></div></header>
      <main className="main-content">
        {sessionError && <div className="top-callout"><ErrorBox error={sessionError} /></div>}
        {auth.blockers.length > 0 && <div className="notice top-callout">当前服务状态：{auth.blockers.join('；')}</div>}
        <RouteContent key={user.companyId ?? 'global'} path={path} auth={auth} navigate={navigate} globalMode={globalMode} />
      </main>
    </div>
  </div>;
}

function LoginScreen({ auth, error, onLogin }: { auth: AuthMeDTO | null; error: Error | null; onLogin: (input: { loginAccount: string; password: string; role: 'employee' | 'company_admin' }) => Promise<void> }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'employee' | 'company_admin'>('employee');
  const [busy, setBusy] = useState(false);
  const [loginError, setLoginError] = useState<Error | null>(null);
  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault(); setBusy(true); setLoginError(null);
    try { await onLogin({ loginAccount: username, password, role }); } catch (reason) { setLoginError(reason instanceof Error ? reason : new Error('登录失败')); }
    finally { setBusy(false); }
  }
  return <div className="login-screen">
    <section className="login-brand-panel"><div className="brand"><div className="brand-mark">W</div><div><div className="brand-name">WB ERP</div><div className="brand-caption">采集与发布工作台</div></div></div><div className="login-message"><h1>把采集、商品准备<br />和多店发布串起来。</h1><p>一处查看员工商品库、预处理任务和每家店铺的卡片结果。状态与操作结果均来自 ERP 服务端。</p></div><div className="login-foot">WBERP · 首期采集发布</div></section>
    <section className="login-form-side"><form className="login-form" onSubmit={(event) => void submit(event)}><h2>登录 ERP</h2><p>使用身份服务中的公司、员工或超级管理员账号。</p>
      <div className="login-fields"><div className="field"><label htmlFor="username">账号</label><input id="username" autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} required /></div><div className="field"><label htmlFor="password">密码</label><input id="password" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></div><div className="field"><label htmlFor="role">登录身份</label><select id="role" className="select" value={role} onChange={(event) => setRole(event.target.value as 'employee' | 'company_admin')}><option value="employee">员工</option><option value="company_admin">公司管理员</option></select></div><button className="button button-primary" disabled={busy}>{busy ? '登录中…' : '登录'}</button></div>
      {loginError && <div style={{ marginTop: 14 }}><ErrorBox error={loginError} /></div>}
      {error && <div style={{ marginTop: 14 }}><ErrorBox error={error} /></div>}
      {auth && <div style={{ marginTop: 20 }}><ApiStatus available={auth.authAvailable} blockers={auth.blockers} /></div>}
    </form></section>
  </div>;
}

function SuperAdminCompanySwitcher({ user, onSwitched }: { user: NonNullable<AuthMeDTO['user']>; onSwitched: () => Promise<void> }) {
  const [companies, setCompanies] = useState<Array<{ id: string; name: string; status: 'active' | 'disabled' }>>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    setLoading(true);
    void api.getSuperAdminCompanies().then((result) => {
      if (!current) return;
      setCompanies(result.items);
      setError(null);
    }).catch((reason) => {
      if (current) setError(reason instanceof Error ? reason.message : '读取公司列表失败');
    }).finally(() => {
      if (current) setLoading(false);
    });
    return () => { current = false; };
  }, [user.id]);

  async function switchCompany(companyId: string | null): Promise<void> {
    if (companyId === user.companyId) return;
    setBusy(true);
    setError(null);
    try {
      await api.switchSuperAdminCompany(companyId);
      await onSwitched();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '切换公司失败');
    } finally {
      setBusy(false);
    }
  }

  return <div className="super-admin-switcher">
    <label className="sr-only" htmlFor="super-admin-company">切换代管公司</label>
    <select id="super-admin-company" className="select" value={user.companyId ?? ''} disabled={loading || busy} onChange={(event) => void switchCompany(event.target.value || null)}>
      <option value="">{loading ? '读取公司…' : '全部公司（只读）'}</option>
      {companies.map((company) => <option key={company.id} value={company.id} disabled={company.status !== 'active'}>
        {company.name}{company.status === 'disabled' ? '（已停用）' : ''}
      </option>)}
    </select>
    {error && <span className="super-admin-switch-error" role="alert">{error}</span>}
  </div>;
}

function RouteContent({ path, auth, navigate, globalMode }: { path: string; auth: AuthMeDTO; navigate: (path: string) => void; globalMode: boolean }) {
  const globalPageIds = new Set(['/overview', '/', '/orders', '/products', '/preprocess', '/publish-queue', '/batches', '/order-profit']);
  if (globalMode && !globalPageIds.has(path) && !path.startsWith('/products/') && !path.startsWith('/batches/')) return <section className="card card-pad"><h1 className="page-title">全局视图只提供只读工作台</h1><p className="page-description">切换到具体公司后可使用公司配置、店铺管理和其他操作。</p></section>;
  if (path === '/overview' || path === '/') return <OverviewPage navigate={navigate} globalMode={globalMode} />;
  if (path === '/orders') return <OrdersPage globalMode={globalMode} />;
  if (path === '/products') return <ProductLibraryPage auth={auth} navigate={navigate} globalMode={globalMode} />;
  if (path.startsWith('/products/')) return <ProductDetailPage auth={auth} navigate={navigate} id={decodeURIComponent(path.slice('/products/'.length))} globalMode={globalMode} />;
  if (path === '/preprocess') return <QueuePage auth={auth} navigate={navigate} queue="preprocess" globalMode={globalMode} />;
  if (path === '/publish-queue') return <QueuePage auth={auth} navigate={navigate} queue="publish" globalMode={globalMode} />;
  if (path === '/batches') return <BatchesPage navigate={navigate} globalMode={globalMode} />;
  if (path.startsWith('/batches/')) return <BatchDetailPage id={decodeURIComponent(path.slice('/batches/'.length))} navigate={navigate} globalMode={globalMode} />;
  if (path === '/shops') return <ShopsPage auth={auth} />;
  if (path === '/order-profit') return auth.user?.role === 'company_admin' || auth.user?.role === 'super_admin'
    ? globalMode ? <CompanyOrderProfitPage key={auth.user?.id} scopeKey={auth.user?.id ?? ''} /> : <OrderProfitPage />
    : <section className="card card-pad"><h1 className="page-title">仅管理员可查看订单利润</h1><p className="page-description">请使用公司管理员账号进入此页面。</p></section>;
  if (path === '/settings/company' && (auth.user?.role === 'company_admin' || auth.user?.role === 'super_admin')) return <CompanySettingsPage />;
  if (path === '/settings/dianxiaomi' && (auth.user?.role === 'company_admin' || auth.user?.role === 'super_admin')) return <DianxiaomiSettingsPage />;
  if (path === '/settings/employee') return auth.user?.role === 'employee' ? <EmployeeSettingsPage /> : <section className="card card-pad"><h1 className="page-title">员工默认设置</h1><p className="page-description">此页按员工账号保存采集和定价参数。请使用对应员工账号登录；公司管理员可在公司服务配置中维护公司设置。</p><button className="button" onClick={() => navigate('/settings/company')}>打开公司服务配置</button></section>;
  if (path === '/settings/extension') return <ExtensionSettingsPage auth={auth} />;
  if (path === '/trash') return <ProductLibraryPage auth={auth} navigate={navigate} trash />;
  if (path === '/logs') return <LogsPage />;
  return <section className="card card-pad"><h1 className="page-title">页面不存在</h1><p className="page-description">此地址无法打开。</p><button className="button button-primary" style={{ marginTop: 16 }} onClick={() => navigate('/overview')}>返回工作总览</button></section>;
}
